import React from "react";
import { PageShell } from "../components/Layout";
import { api } from "../services/api";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import useSeo from "../hooks/useSeo";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Today's date in India/IST as a local Date at midnight
function istToday() {
  const iso = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function ClubChart() {
  const [date, setDate] = React.useState(istToday());
  useSeo({
    title: "Sunday Ground Teer Club Chart | Date-wise Result Lookup",
    description:
      "Sunday Ground Teer Club Chart - pick any date from the calendar to instantly see that day's F/R and S/R result. Today's result shown automatically.",
  });
  const [open, setOpen] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const maxDate = istToday();

  React.useEffect(() => {
    setLoading(true);
    api
      .resultByDate(toISO(date))
      .then(async (r) => {
        if (r && r.fr && r.sr) {
          setResult(r);
          return;
        }
        // No result published for this date — show the latest published result
        try {
          const latest = await api.latestResult();
          setResult(latest && latest.fr && latest.sr ? latest : null);
        } catch {
          setResult(null);
        }
      })
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [date]);

  const declared = !loading && result;

  return (
    <PageShell title="Teer Club Chart" subtitle="Today's result shown automatically — or pick any date">
      <div className="max-w-md mx-auto">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#1f2733] text-white font-bold hover:bg-black transition-colors">
              <CalendarIcon className="w-5 h-5" />
              {date.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                if (d) setDate(d);
                setOpen(false);
              }}
              disabled={(d) => d > maxDate || d < new Date(2019, 0, 1)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <div className="mt-6 border border-gray-300 rounded-xl overflow-hidden bg-white shadow-sm">
          <div className="text-center font-black text-lg py-3 bg-white border-b border-gray-300">
            {result ? result.date : date.toLocaleDateString("en-GB")}
          </div>
          <div className="text-center font-bold text-lg py-2 bg-gray-100 border-b border-gray-300">
            SUNDAY TEER
          </div>
          <div className="grid grid-cols-2 border-b border-gray-300">
            <div className="text-center font-bold py-2 bg-[#3bd4bd] border-r border-gray-300">
              F/R (4:30 PM)
            </div>
            <div className="text-center font-bold py-2 bg-[#3bd4bd]">S/R (5:30 PM)</div>
          </div>
          {declared ? (
            <div className="grid grid-cols-2">
              <div className="text-center font-black text-3xl py-5 border-r border-gray-300">
                {result.fr}
              </div>
              <div className="text-center font-black text-3xl py-5">{result.sr}</div>
            </div>
          ) : (
            <div className="text-center font-black text-lg sm:text-xl py-6 text-[#c53a12]">
              {loading ? "…" : "Result Not Declared Yet"}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
